import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, Stack } from '@mantine/core';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FileInputControl, Form } from '@/core/components';

import { DOCUMENT_UPLOAD_ACCEPT, MAX_UPLOAD_SIZE_MB, useUploadNotification } from '@feature/utils/upload/fileUpload';

import { useTravelRequestAddFile } from '../../model/TravelRequestQuery';
import { TravelActivityType, TravelFileKind, travelFileKindList } from '../../model/TravelRequestTypes';
import { TravelFileKindSelectControl } from '../form/TravelFileKindSelectControl';

interface TravelRequestFileUploadModalProps {
  requestId: number;
  activityType: TravelActivityType;
  kinds?: readonly TravelFileKind[];
  onClose: () => void;
}

const schema = z.object({
  kind: z.enum(travelFileKindList),
  file: z.custom<File>(value => value instanceof File, { params: { i18n: 'custom.fileRequired' } }),
});

type TravelFileUploadSchema = z.infer<typeof schema>;

export function TravelRequestFileUploadModal({
  requestId,
  activityType,
  kinds,
  onClose,
}: TravelRequestFileUploadModalProps) {
  const { t } = useTranslation();
  const notification = useUploadNotification();
  const { mutateAsync, isPending } = useTravelRequestAddFile(requestId);

  const form = useForm<TravelFileUploadSchema>({
    resolver: zodResolver(schema),
    defaultValues: { kind: kinds?.length === 1 ? kinds[0] : undefined },
  });
  const { setValue } = form;
  const selectedKind = useWatch({ control: form.control, name: 'kind' });
  const kindWithdrawn = selectedKind != null && kinds != null && !kinds.includes(selectedKind);

  // The offered kinds follow the request, which can change under an open dialog.
  useEffect(() => {
    if (kindWithdrawn) {
      setValue('kind', null as unknown as TravelFileKind);
    }
  }, [kindWithdrawn, setValue]);

  // Promise handlers still run after an unmount, unlike mutate-level callbacks.
  const handleSubmit = (values: TravelFileUploadSchema) =>
    mutateAsync(values).then(() => {
      notification.success();
      onClose();
    }, notification.error);

  const handleClose = () => {
    if (!isPending) onClose();
  };

  return (
    <Modal
      opened
      centered
      onClose={handleClose}
      closeButtonProps={{ disabled: isPending }}
      title={t('travel.requests.view.uploadFile')}
      size="md"
    >
      <Form
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={handleClose}
        isSubmitting={isPending}
      >
        <Stack gap="md">
          <TravelFileKindSelectControl
            name="kind"
            kinds={kinds}
            activityType={activityType}
            label={t('travel.requests.view.fileKind')}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
          <FileInputControl
            name="file"
            label={t('travel.requests.view.file')}
            description={t('travel.requests.view.fileHint', { maxSize: MAX_UPLOAD_SIZE_MB })}
            accept={DOCUMENT_UPLOAD_ACCEPT}
            isSubmitting={isPending}
            withAsterisk
            control={form.control}
          />
        </Stack>
      </Form>
    </Modal>
  );
}
